import React from 'react';
import { motion } from 'framer-motion';

interface TimelineEvent {
  date?: string;
  title: string;
  description?: string;
  icon?: string;
  color?: string;
}

interface TimelineVisualizationProps {
  data: TimelineEvent[];
  config: {
    title?: string;
    description?: string;
    explanation?: string;
    direction?: 'vertical' | 'horizontal';
  };
  preview?: boolean;
  collapsed?: boolean;
}

const TIMELINE_COLORS = [
  'var(--primary-color)',
  'hsl(var(--chart-6))',
  'hsl(var(--chart-7))',
  'hsl(var(--chart-8))',
  'hsl(var(--chart-9))',
  'hsl(var(--chart-10))',
];

const TimelineVisualization: React.FC<TimelineVisualizationProps> = ({
  data,
  config,
  preview = false,
  collapsed = false,
}) => {
  if (!data || data.length === 0) {
    return <div className="viz-unsupported">No timeline data provided</div>;
  }

  return (
    <div className={`viz-timeline-wrapper ${preview ? 'viz-preview' : ''}`}>
      {!preview && config.title && (
        <div className="viz-timeline-header">
          <h3 className="viz-timeline-title">{config.title}</h3>
          {config.description && <p className="viz-timeline-description">{config.description}</p>}
        </div>
      )}
      <motion.div
        initial={false}
        animate={{
          height: collapsed ? 0 : 'auto',
          opacity: collapsed ? 0 : 1,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: 'easeOut' },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className="viz-timeline">
          {data.map((event, index) => {
            const color = event.color || TIMELINE_COLORS[index % TIMELINE_COLORS.length];
            return (
              <div key={index} className="viz-timeline-item">
                <div className="viz-timeline-marker-col">
                  <div className="viz-timeline-dot" style={{ backgroundColor: color, boxShadow: `0 0 0 4px ${color}22` }} />
                  {index < data.length - 1 && <div className="viz-timeline-line" />}
                </div>
                <div className="viz-timeline-content">
                  {event.date && <span className="viz-timeline-date">{event.date}</span>}
                  <h4 className="viz-timeline-event-title">{event.title}</h4>
                  {event.description && !preview && (
                    <p className="viz-timeline-event-desc">{event.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {!preview && config.explanation && (
          <div className="viz-timeline-footer">
            <span>{config.explanation}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TimelineVisualization;
